import EventIcon from '@mui/icons-material/Event'
import EventRepeatIcon from '@mui/icons-material/EventRepeat'
import GradingIcon from '@mui/icons-material/Grading'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'

import type { AuthenticatedUser } from '../../../auth/types/AuthenticatedUser'
import { type Step, Stepper } from '../../../theme/components/Stepper'
import type { AssignedBijlage } from '../../bijlagen/types/AssignedBijlage'
import type { Bijlage } from '../../bijlagen/types/Bijlage'
import type { Subproces } from '../types/Subproces'
import {
  canEditSchouw,
  generateSchouwStepStatuses,
  getSchouwBeoordelenCaption,
  getSchouwDoneCaption,
  getSchouwPlannenCaption,
} from '../util/subproces-schouw-util'
import {
  getStatusLabelForType,
  getStatusTransitions,
} from '../util/subproces-util'

import { SubprocesCard } from './SubprocesCard'

export { SubprocesSchouw }

type Props = {
  subproces: Subproces
  subprocessen: Subproces[]
  user: AuthenticatedUser
  isVerantwoordelijke: boolean
  assignedBijlagen: AssignedBijlage[]
  bijlagen: Bijlage[]
  openLogDrawer: (subproces: Subproces) => void

  onClickPlan: () => void
  onClickReview: () => void
  onClickHerplan: () => void
  onClickCancel: () => void
  onClickPause: () => void
  onClickResume: () => void

  onClickCreateBijlage: () => void
  onClickUploadBijlage: (assignedBijlage: AssignedBijlage) => void
  onClickCancelBijlage: (assignedBijlage: AssignedBijlage | Bijlage) => void
  onClickOpleveren: (assignedBijlage: AssignedBijlage) => void
  onClickReviewBijlage: (assignedBijlage: AssignedBijlage) => void
  onClickEditBijlage: (assignedBijlage: AssignedBijlage) => void
  onClickOpenBijlage: (assignedBijlage: Bijlage) => void
}

const SubprocesSchouw = ({
  subproces,
  subprocessen,
  user,
  isVerantwoordelijke,
  assignedBijlagen,
  bijlagen,
  openLogDrawer,

  onClickPlan,
  onClickReview,
  onClickHerplan,
  onClickCancel,
  onClickPause,
  onClickResume,

  onClickCreateBijlage,
  onClickUploadBijlage,
  onClickCancelBijlage,
  onClickOpleveren,
  onClickReviewBijlage,
  onClickEditBijlage,
  onClickOpenBijlage,
}: Props) => {
  const { statuses } = generateSchouwStepStatuses({ subproces })
  const canEdit = canEditSchouw({ user, subproces })
  const transitions = getStatusTransitions(subproces)
  const doneCaption = getSchouwDoneCaption({ subproces })

  const steps: Step[] = [
    {
      label: 'Schouw plannen',
      status: statuses.created,
      caption: getSchouwPlannenCaption({ subproces }),
      content: (
        <>
          <Typography>
            Plan de schouw in door een uitvoerdatum vast te leggen. Zodra de
            datum bekend is kan de schouw worden uitgevoerd en beoordeeld.
          </Typography>

          {canEdit && transitions.planning && (
            <Button
              variant="contained"
              startIcon={<EventIcon />}
              onClick={onClickPlan}
            >
              Schouw plannen
            </Button>
          )}
        </>
      ),
    },
    {
      label: 'Schouw beoordelen',
      status: statuses.planned,
      caption: getSchouwBeoordelenCaption({ subproces }),
      content: (
        <>
          <Typography>
            De schouw is ingepland. Zodra de schouw is uitgevoerd kan deze
            worden beoordeeld en goed- of afgekeurd.
          </Typography>

          {canEdit && (transitions.herplan || transitions.review) && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {transitions.herplan && (
                <Button
                  variant="text"
                  startIcon={<EventRepeatIcon />}
                  onClick={onClickHerplan}
                >
                  Herplannen
                </Button>
              )}

              {transitions.review && (
                <Button
                  variant="contained"
                  startIcon={<GradingIcon />}
                  onClick={onClickReview}
                >
                  Beoordelen
                </Button>
              )}
            </Box>
          )}
        </>
      ),
    },
    {
      label: getStatusLabelForType('schouw', 'done'),
      status: statuses.done,
      content: doneCaption ? <Typography>{doneCaption}</Typography> : <></>,
    },
  ]

  return (
    <SubprocesCard
      subproces={subproces}
      subprocessen={subprocessen}
      user={user}
      openLogDrawer={openLogDrawer}
      onClickCancel={onClickCancel}
      onClickPause={onClickPause}
      onClickResume={onClickResume}
      disableCancel={!canEdit || !transitions.cancel}
      disablePause={!canEdit || !transitions.pause}
      assignedBijlagen={assignedBijlagen}
      bijlagen={bijlagen}
      isVerantwoordelijke={isVerantwoordelijke}
      onClickCreateBijlage={onClickCreateBijlage}
      onClickUploadBijlage={onClickUploadBijlage}
      onClickCancelBijlage={onClickCancelBijlage}
      onClickOpleveren={onClickOpleveren}
      onClickReviewBijlage={onClickReviewBijlage}
      onClickEditBijlage={onClickEditBijlage}
      onClickOpenBijlage={onClickOpenBijlage}
    >
      <Stepper steps={steps} />
    </SubprocesCard>
  )
}
